import { Text } from '@kk/ui';
import { useEffect, useState } from 'react';

import { deck } from '@/copy/deck';

/**
 * **WHERE A PARENT GOES FROM A WEB PAGE — and it is never the app itself.**
 *
 * This replaced a button that sent people to `https://app.kiddiekredit.com`. That host is the universal-link
 * domain: on a phone with the app installed iOS and Android intercept it, and **everywhere else it is the API,
 * which answers `GET /` with `{"error":{"code":"NOT_FOUND"}}`**. Measured on 2026-09-22 — a parent on a laptop
 * who pressed "Open the app" was shown raw JSON. The general rule Evan drew from it: *"A web page must never
 * link to the app's scheme, because it only resolves on a phone with the app installed."*
 *
 * So the destination depends on what the reader is holding:
 *  · **a phone** can install the app, so it gets one button to `link.kreditacademy.com`, which routes to the
 *    right store;
 *  · **a desktop** cannot, so it gets the two store listings side by side — the person reads them, and installs
 *    on the device that can.
 *
 * `noBoardSampleShipped` has a sibling guard for this: no href in the built bundle may carry a custom scheme.
 */
const PHONE = '(max-width: 1018px)';

/** Re-renders when the viewport crosses the breakpoint, so a resized window is not left with the wrong link. */
export function useIsPhone(): boolean {
  const [phone, setPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(PHONE).matches === true,
  );
  useEffect(() => {
    const mq = window.matchMedia?.(PHONE);
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setPhone(e.matches);
    setPhone(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return phone;
}

/**
 * The store links. `variant="button"` draws the wasabi CTA a phone gets; on a desktop both variants draw the
 * two store links, because there is nothing a button could usefully do there.
 */
export function GetTheApp({ testID, secondary }: { testID?: string; secondary?: boolean }) {
  const phone = useIsPhone();
  if (phone)
    return (
      <a
        // `secondary`: beside the card's own primary button (staff sign-in's Continue) the parent's way out is the
        // outline button, so a phone never shows two wasabi buttons on one card (the 23 Oct audit, T2-16)
        className={`wasabi-link${secondary ? ' outline' : ''}`}
        href={deck.getTheApp.oneLink}
        data-testid={testID ?? 'get-the-app'}
      >
        <Text variant="button" tone="ink">
          {deck.getTheApp.cta}
        </Text>
      </a>
    );
  return (
    <div className="store-links" data-testid={testID ?? 'get-the-app'}>
      {/*
       * **The official badge artwork is not in this repo**, and a hand-drawn App Store or Google Play lockup is
       * a different mark — both vendors require their own file, unmodified. Until those two files land these
       * are the same two destinations as bordered links, which work; swapping in an <img> is a one-line change
       * at each of them.
       */}
      <a className="store-link" href={deck.getTheApp.appleUrl} data-testid="store-apple">
        <Text variant="bodySm" tone="ink">
          {deck.getTheApp.apple}
        </Text>
      </a>
      <a className="store-link" href={deck.getTheApp.googleUrl} data-testid="store-google">
        <Text variant="bodySm" tone="ink">
          {deck.getTheApp.google}
        </Text>
      </a>
    </div>
  );
}
